/**
 * Observer MSP/user scope model.
 *
 * UI stores explicit scope modes instead of inferring "all" vs "none" from array length.
 * MSP and user scopes are independent: changing one never resets the other.
 */

export type ObserverScopeMode = 'all' | 'none' | 'some';

export interface ObserverScope {
  mode: ObserverScopeMode;
  /** Populated when mode === 'some'. */
  values: string[];
}

export const OBSERVER_SCOPE_ALL: ObserverScope = { mode: 'all', values: [] };
export const OBSERVER_SCOPE_NONE: ObserverScope = { mode: 'none', values: [] };

export function scopeFromSelection(allValues: string[], selected: string[]): ObserverScope {
  const valid = selected.filter((value) => allValues.includes(value));
  if (allValues.length === 0) {
    return valid.length === 0 ? OBSERVER_SCOPE_NONE : { mode: 'some', values: valid };
  }
  if (valid.length === 0) {
    return OBSERVER_SCOPE_NONE;
  }
  if (allValues.every((value) => valid.includes(value))) {
    return OBSERVER_SCOPE_ALL;
  }
  return { mode: 'some', values: valid };
}

export function scopeToSelection(scope: ObserverScope, allValues: string[]): string[] {
  if (allValues.length === 0) {
    return scope.mode === 'some' ? scope.values : [];
  }
  switch (scope.mode) {
    case 'all':
      return [...allValues];
    case 'none':
      return [];
    case 'some':
      return scope.values.filter((value) => allValues.includes(value));
    default:
      return [];
  }
}

/**
 * Drop user ids that are not in the refreshed option list. Preserves all/none intent.
 */
export function pruneScope(scope: ObserverScope, allValues: string[]): ObserverScope {
  if (scope.mode === 'all' || scope.mode === 'none') {
    return scope;
  }
  const valid = scope.values.filter((value) => allValues.includes(value));
  if (valid.length === 0) {
    return OBSERVER_SCOPE_NONE;
  }
  if (allValues.length > 0 && allValues.every((value) => valid.includes(value))) {
    return OBSERVER_SCOPE_ALL;
  }
  return { mode: 'some', values: valid };
}

export function mspScopeToRequestIds(allMspIds: number[], scope: ObserverScope): number[] {
  switch (scope.mode) {
    case 'all':
      return allMspIds;
    case 'none':
      return [];
    case 'some':
      return scope.values
        .map((value) => Number(value))
        .filter((id) => Number.isInteger(id) && id > 0);
    default:
      return [];
  }
}

/** undefined = all users, null = none, string[] = subset */
export function userScopeToRequestIds(scope: ObserverScope): string[] | null | undefined {
  switch (scope.mode) {
    case 'all':
      return undefined;
    case 'none':
      return null;
    case 'some':
      return scope.values;
    default:
      return null;
  }
}
